/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';

import type { AtpStatus } from '@/lib/atp/atp-fields';
import { resolveAtpDeliveryMessage } from '@/lib/atp/atp-delivery-message';
import { formatDeliveryDate } from '@/lib/date-utils';
import { cn } from '@/lib/utils';

interface BackorderNoticeProps {
    /** The basket / order product item carrying the `c_atp*` fields. */
    productItem: unknown;
    /** Optional basket-level ATP status gate — see `resolveAtpDeliveryMessage`. Omit on surfaces with no basket-status concept (e.g. order confirmation). */
    basketStatus?: AtpStatus | null;
    /**
     * When true, use the plural title ("These items are currently on backorder.")
     * — for group-level notices that cover more than one line (e.g. order review).
     */
    plural?: boolean;
    className?: string;
}

/**
 * Renders an expandable "This item is currently on backorder" notice for a
 * cart / mini-cart / order-confirmation line item.
 *
 * Replaces the old inline "Backordered — estimated {range}" copy that used
 * to render under the cart's quantity picker (see `<CartDeliveryEstimate>`).
 * This component owns only the backorder case: it renders `null` for every
 * other ATP variant (`getItBy` / `shipsBy` / `unavailable`) or when there is
 * nothing to show, so it can be dropped in next to an existing alert (or on
 * its own) without any variant branching at the call site.
 *
 * Generic over any surface carrying `c_atp*` fields — basket product items,
 * order product items, and synthetic kit-aggregate items (see
 * `aggregateKitAtpFields`) all work identically, since `resolveAtpDeliveryMessage`
 * reads the fields structurally and only applies the `basketStatus` gate when
 * a caller explicitly passes one.
 *
 * Uses the real ATP-provided backorder date range when available, falling
 * back to the generic "8-14 weeks" copy when ATP has no range for this item.
 */
export default function BackorderNotice({
    productItem,
    basketStatus,
    plural = false,
    className,
}: BackorderNoticeProps): ReactElement | null {
    const { t, i18n } = useTranslation('delivery');
    const [expanded, setExpanded] = useState(true);

    const message = resolveAtpDeliveryMessage(productItem, basketStatus);
    if (!message || message.variant !== 'backordered') {
        return null;
    }

    const locale = i18n.language;
    const start = formatDeliveryDate(message.rangeStart ?? undefined, locale) ?? message.rangeStart;
    const end = formatDeliveryDate(message.rangeEnd ?? undefined, locale) ?? message.rangeEnd;
    const range = start && end ? `${start} \u2013 ${end}` : start || end;

    const description = range ? t('backorderEstimateRange', { range }) : t('backorderEstimateDefault');
    const title = plural ? t('backorderTitlePlural') : t('backorderTitle');

    return (
        <div className={cn('text-sm', className)} data-testid="backorder-notice">
            <button
                type="button"
                className="inline-flex items-center gap-1.5 font-medium text-destructive text-left"
                aria-expanded={expanded}
                onClick={() => setExpanded((prev) => !prev)}>
                <span>{title}</span>
                {expanded ? (
                    <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
                ) : (
                    <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                )}
            </button>
            {expanded ? <p className="mt-1 text-foreground text-xs">{description}</p> : null}
        </div>
    );
}
